import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Estudiante } from '../../models/estudiante.model';
import { Instructor } from '../../models/instructor.model';
import { Graduacion } from '../../models/graduacion.model';
import { CentroEntrenamiento } from '../../models/centro-entrenamiento.model';
import { EstudianteService } from '../../services/estudiante.service';
import { SharedService } from '../../services/shared.service';
import { GeografiaService } from '../../services/geografia.service';

@Component({
  selector: 'app-estudiantes-list',
  templateUrl: './estudiantes-list.component.html',
  styleUrls: ['./estudiantes-list.component.css']
})
export class EstudiantesListComponent implements OnInit {
  Math = Math;
  
  searchForm: FormGroup;
  estudiantes: Estudiante[] = [];
  instructores: Instructor[] = [];
  graduaciones: Graduacion[] = [];
  centros: CentroEntrenamiento[] = [];
  filteredEstudiantes: Estudiante[] = [];
  selectedEstudiante: Estudiante | null = null;
  showDetail: boolean = false;
  loading: boolean = false;
  errorMessage: string | null = null;

  // Sorting properties
  sortBy: string = 'nroLibreta';
  sortOrder: 'asc' | 'desc' = 'asc';
  availableSortFields = [
    'nroLibreta', 'apellido', 'nombre', 'documento', 'fcNacimiento',
    'nacionalidad', 'localidad', 'provincia', 'instructor', 
    'centroEntrenamiento', 'fcInicio', 'graduacion', 'fcUltimoExamen'
  ];
  
  // Pagination properties
  pageSize: number = 100;
  currentPage: number = 1;
  totalPages: number = 1;
  paginatedEstudiantes: Estudiante[] = [];

  // Date limits for validation
  readonly TODAY = new Date().toISOString().split('T')[0];
  readonly MIN_BIRTH_DATE = '1900-01-01';
  readonly MAX_AGE_YEARS = 100;
  
  constructor(
    private fb: FormBuilder,
    private estudianteService: EstudianteService,
    private sharedService: SharedService,
    private geographicService: GeografiaService
  ) {
    this.searchForm = this.fb.group({
      nroLibretaDesde: ['', [Validators.min(1)]],
      nroLibretaHasta: [''],
      apellido: [''],
      nombre: [''],
      fcNacimientoDesde: [''],
      fcNacimientoHasta: [''],
      documento: [''],
      nacionalidad: [''],
      localidad: [''],
      provincia: [''],
      idInstructor: [''],
      idCentroEntrenamiento: [''],
      fcInicioDesde: [''],
      fcInicioHasta: [''],
      idGraduacion: [''],
      fcUltimoExamenDesde: [''],
      fcUltimoExamenHasta: [''],
      busquedaEspecial: ['']
    });

    // Add form value change listeners for range validation
    this.setupRangeValidation();
  }

  ngOnInit(): void {
    this.loadFilterData();
  }

  // Setup range validation listeners
  private setupRangeValidation(): void {
    // Nro Libreta range validation
    this.searchForm.get('nroLibretaDesde')?.valueChanges.subscribe(() => {
      this.validateLibretaRange();
    });
    this.searchForm.get('nroLibretaHasta')?.valueChanges.subscribe(() => {
      this.validateLibretaRange();
    });

    // Birth date range validation
    this.searchForm.get('fcNacimientoDesde')?.valueChanges.subscribe(() => {
      this.validateBirthDateRange();
      // Revalidate dependent ranges
      this.validateStartDateRange();
      this.validateLastExamRange();
    });
    this.searchForm.get('fcNacimientoHasta')?.valueChanges.subscribe(() => {
      this.validateBirthDateRange();
      // Revalidate dependent ranges
      this.validateStartDateRange();
      this.validateLastExamRange();
    });

    // Start date range validation
    this.searchForm.get('fcInicioDesde')?.valueChanges.subscribe(() => {
      this.validateStartDateRange();
      // Revalidate dependent ranges
      this.validateLastExamRange();
    });
    this.searchForm.get('fcInicioHasta')?.valueChanges.subscribe(() => {
      this.validateStartDateRange();
      // Revalidate dependent ranges
      this.validateLastExamRange();
    });

    // Last exam date range validation
    this.searchForm.get('fcUltimoExamenDesde')?.valueChanges.subscribe(() => {
      this.validateLastExamRange();
    });
    this.searchForm.get('fcUltimoExamenHasta')?.valueChanges.subscribe(() => {
      this.validateLastExamRange();
    });
  }

  // Validate Nro Libreta range
  private validateLibretaRange(): void {
    const desde = this.searchForm.get('nroLibretaDesde')?.value;
    const hasta = this.searchForm.get('nroLibretaHasta')?.value;

    if (desde && hasta && parseInt(desde) > parseInt(hasta)) {
      this.searchForm.get('nroLibretaHasta')?.setErrors({ rangeInvalid: true });
    } else {
      // Clear the range error but keep other errors
      const currentErrors = this.searchForm.get('nroLibretaHasta')?.errors;
      if (currentErrors?.['rangeInvalid']) {
        delete currentErrors['rangeInvalid'];
        const hasOtherErrors = Object.keys(currentErrors).length > 0;
        this.searchForm.get('nroLibretaHasta')?.setErrors(hasOtherErrors ? currentErrors : null);
      }
    }
  }

  private validateDateRange(
      desdeFieldName: string, 
      hastaFieldName: string, 
      options: {
        allowFuture?: boolean;
        minDateFieldName?: string; // Field that this range should be after
        errorPrefix?: string; // Custom prefix for error messages
      } = {}
    ): void {
      const desde = this.searchForm.get(desdeFieldName)?.value;
      const hasta = this.searchForm.get(hastaFieldName)?.value;

      // Clear existing range-related errors for both fields
      this.clearSpecificErrors(desdeFieldName, ['rangeInvalid', 'futureDate', 'beforeMinDate']);
      this.clearSpecificErrors(hastaFieldName, ['rangeInvalid', 'futureDate']);

      // Validate dates are not in the future (unless allowFuture is true)
      if (!options.allowFuture) {
        if (desde && desde > this.TODAY) {
          this.setError(desdeFieldName, 'futureDate');
        }

        if (hasta && hasta > this.TODAY) {
          this.setError(hastaFieldName, 'futureDate');
        }
      }

      // Validate range order - only if both dates are present and valid
      if (desde && hasta) {
        const desdeValid = options.allowFuture || desde <= this.TODAY;
        const hastaValid = options.allowFuture || hasta <= this.TODAY;
        
        if (desdeValid && hastaValid && desde > hasta) {
          this.setError(hastaFieldName, 'rangeInvalid');
        }
      }

      // Validate against minimum date field if specified
      if (options.minDateFieldName) {
        const minDate = this.searchForm.get(options.minDateFieldName)?.value;
        const minDateValid = !minDate || options.allowFuture || minDate <= this.TODAY;
        
        if (desde && minDate && minDateValid) {
          const desdeValid = options.allowFuture || desde <= this.TODAY;
          if (desdeValid && desde < minDate) {
            this.setError(desdeFieldName, 'beforeMinDate');
          }
        }
      }
    }

    // Helper method to set a specific error
  private setError(controlName: string, errorType: string): void {
    const control = this.searchForm.get(controlName);
    if (control) {
      const currentErrors = control.errors || {};
      currentErrors[errorType] = true;
      control.setErrors(currentErrors);
    }
  }

  // Helper method to clear specific errors
  private clearSpecificErrors(controlName: string, errorTypes: string[]): void {
    const control = this.searchForm.get(controlName);
    if (control?.errors) {
      const currentErrors = { ...control.errors };
      
      errorTypes.forEach(errorType => {
        delete currentErrors[errorType];
      });
      
      const hasOtherErrors = Object.keys(currentErrors).length > 0;
      control.setErrors(hasOtherErrors ? currentErrors : null);
    }
  }

  // Updated validation methods using the reusable function
  private validateBirthDateRange(): void {
    this.validateDateRange('fcNacimientoDesde', 'fcNacimientoHasta', {
      allowFuture: false
    });
  }

  private validateStartDateRange(): void {
    this.validateDateRange('fcInicioDesde', 'fcInicioHasta', {
      allowFuture: false,
      minDateFieldName: 'fcNacimientoDesde' // Start date should be after birth date
    });
  }

  private validateLastExamRange(): void {
    this.validateDateRange('fcUltimoExamenDesde', 'fcUltimoExamenHasta', {
      allowFuture: false,
      minDateFieldName: 'fcInicioDesde' // Exam date should be after start date
    });
  }

  // Helper method to clear specific range errors
  private clearRangeError(controlName: string): void {
    const control = this.searchForm.get(controlName);
    const currentErrors = control?.errors;
    if (currentErrors) {
      delete currentErrors['rangeInvalid'];
      delete currentErrors['futureDate'];
      delete currentErrors['beforeBirth'];
      delete currentErrors['beforeStart'];
      const hasOtherErrors = Object.keys(currentErrors).length > 0;
      control?.setErrors(hasOtherErrors ? currentErrors : null);
    }
  }

  // Check if form has validation errors
  hasValidationErrors(): boolean {
    return this.searchForm.invalid;
  }

  // Get error message for a specific field
  getFieldErrorMessage(fieldName: string): string {
    const control = this.searchForm.get(fieldName);
    if (control?.errors) {
      if (control.errors['min']) {
        return 'El valor mínimo es 1';
      }
      if (control.errors['rangeInvalid']) {
        return 'El valor "hasta" debe ser mayor o igual al valor "desde"';
      }
      if (control.errors['futureDate']) {
        return 'La fecha no puede ser en el futuro';
      }
      if (control.errors['beforeMinDate']) {
        // Determine which type of date relationship this is
        if (fieldName.includes('fcInicio')) {
          return 'La fecha de inicio no puede ser anterior al nacimiento';
        }
        if (fieldName.includes('fcUltimoExamen')) {
          return 'La fecha del examen no puede ser anterior al inicio';
        }
        return 'La fecha no puede ser anterior a la fecha mínima requerida';
      }
    }
    return '';
  }

  // Override the filterEstudiantes method to validate before searching
  filterEstudiantes(): void {
    if (this.hasValidationErrors()) {
      this.errorMessage = 'Por favor corrija los errores en el formulario antes de buscar';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    const filters = this.prepareFilters();

    this.estudianteService.filterEstudiantes(filters, this.sortBy, this.sortOrder).subscribe(
      (data: Estudiante[]) => {
        this.estudiantes = data;
        this.applyFilters();
        this.loading = false;
        this.goToPage(1);
      },
      (error: Error) => {
        console.error('Error searching estudiantes:', error);
        this.errorMessage = 'Error al buscar estudiantes';
        this.loading = false;
      }
    );
  }

  // ... rest of existing methods remain the same ...
  sortCentros(centros: CentroEntrenamiento[]): CentroEntrenamiento[] {
    return centros.sort((a, b) => {
      if (a.activo !== b.activo) {
        return a.activo ? -1 : 1;
      }
      return a.nombre.localeCompare(b.nombre);
    });
  }

  filterNacionalidades = (input: string) => {
    return this.geographicService.filterNacionalidades(input);
  }

  filterLocalidades = (input: string) => {
    return this.geographicService.filterLocalidades(input);
  }

  filterProvincias = (input: string) => {
    return this.geographicService.filterProvincias(input);
  }

  get nacionalidadControl(): AbstractControl {
    return this.searchForm.get('nacionalidad')!;
  }

  get localidadControl(): AbstractControl {
    return this.searchForm.get('localidad')!;
  }

  get provinciaControl(): AbstractControl {
    return this.searchForm.get('provincia')!;
  }

  loadFilterData(): void {
    this.sharedService.graduaciones$.subscribe(
      (graduaciones) => {
        this.graduaciones = graduaciones;
      },
      (error) => {
        console.error('Error loading graduaciones:', error);
        this.errorMessage = 'Error al cargar las graduaciones';
      }
    );

    this.sharedService.instructores$.subscribe(
      (instructores) => {
        this.instructores = instructores;
      },
      (error) => {
        console.error('Error loading instructores:', error);
        this.errorMessage = 'Error al cargar los instructores';
      }
    );

    this.sharedService.centros$.subscribe(
      (centros) => {
        this.centros = this.sortCentros(centros);
      },
      (error) => {
        console.error('Error loading centros de entrenamiento:', error);
        this.errorMessage = 'Error al cargar los centros de entrenamiento';
      }
    );
  }

  onSortChange(field: string): void {
    if (!this.availableSortFields.includes(field)) {
      console.warn(`Invalid sort field: ${field}`);
      return;
    }

    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'asc';
    }
    
    this.filterEstudiantes();
  }

  private prepareFilters(): any {
    const formValues = this.searchForm.value;
    const filters: any = {};

    Object.keys(formValues).forEach(key => {
      if (formValues[key]) {
        if (key.includes('fc') && formValues[key]) {
          filters[key] = this.sharedService.formatDate(formValues[key]);
        } else {
          filters[key] = formValues[key];
        }
      }
    });

    return filters;
  }

  applyFilters(): void {
    this.filteredEstudiantes = [...this.estudiantes];
    this.calculatePagination();
  }

  resetFilters(): void {
    this.searchForm.reset();
    this.filteredEstudiantes = [...this.estudiantes];
    this.calculatePagination();
    this.goToPage(1);
  }

  calculatePagination(): void {
    this.totalPages = Math.ceil(this.filteredEstudiantes.length / this.pageSize);
    if (this.totalPages === 0) this.totalPages = 1;
    this.updatePaginatedResults();
  }

  updatePaginatedResults(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.filteredEstudiantes.length);
    this.paginatedEstudiantes = this.filteredEstudiantes.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedResults();
      const resultsContainer = document.querySelector('.results-container');
      if (resultsContainer) {
        resultsContainer.scrollTop = 0;
        resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const totalPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(totalPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + totalPagesToShow - 1);
    
    if (endPage - startPage + 1 < totalPagesToShow) {
      startPage = Math.max(1, endPage - totalPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  onViewDetail(estudiante: Estudiante): void {
    this.selectedEstudiante = estudiante;
    this.showDetail = true;
  }

  onCloseDetail(): void {
    this.showDetail = false;
    this.selectedEstudiante = null;
    this.filterEstudiantes();
  }
}